import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import LogFoodItemPromptPage from './log_food_item_prompt_page';
import {
  beginLogMealAIOperation,
  checkLogMealAIOperation,
} from '../../operations/meal_operations';
import {
  newMealFromImportedRecipe,
  newMealWithDefaultFields,
} from '../../services/meals';
import { awaitableTimeout } from '../../services/process';
import { JobStatus } from '../../API';
import LogFoodItemMealDetailPage from './log_food_item_meal_detail_page';
import { cloneObject } from '../../operations/utils';
import { mealCompletedActionRecord } from '../../services/my_day';
import {
  useAddUserMyDayActionRecordMutation,
  useCreateMealMutation,
  useGetDbPlannerWeekQuery,
  useLazyGetFullMealQuery,
  useUpdateDbPlannerWeekOptimisticallyMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import {
  dbWeekStartDatesAndDayIndexesCoveringViewWeek,
  plannerWeekWithMealAdded,
} from '../../services/planner';
import { calendarDayFromJSDate } from '../../services/dates';
import { useTracker } from '../../operations/tracking_operations';
import {
  useCurrentProgrammeEnrollmentQuery,
  useGetCurrentSpaceMembershipQuery,
  useGetRecentlyLoggedDistilledMealsQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';

const PROMPT_PAGE = 'promptPage';
const MEAL_DETAIL_PAGE = 'mealDetailPage';

const LogFoodItemContainer = ({
  plannerViewWeekStartDate,
  dayIndex,
  onDismiss,
}) => {
  const [page, setPage] = useState(PROMPT_PAGE);
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [meal, setMeal] = useState(null);

  const tracker = useTracker();

  const { data: userProfile } = useGetUserProfileQuery();

  const [createMeal] = useCreateMealMutation();

  const [addUserMyDayActionRecord] = useAddUserMyDayActionRecordMutation();

  const [updateDbPlannerWeek] = useUpdateDbPlannerWeekOptimisticallyMutation();

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const { data: currentProgrammeEnrollment } =
    useCurrentProgrammeEnrollmentQuery();

  const startDatesAndDayIndexes = dbWeekStartDatesAndDayIndexesCoveringViewWeek(
    plannerViewWeekStartDate,
  );

  const { data: plannerWeek } = useGetDbPlannerWeekQuery(
    startDatesAndDayIndexes[0].dbWeekStartDate,
    {
      skip: !startDatesAndDayIndexes[0].dbWeekStartDate,
    },
  );

  const { data: recentlyLoggedDistilledMeals } =
    useGetRecentlyLoggedDistilledMealsQuery();

  // const onChangeMeal = (newMeal) => {
  //   setMeal(newMeal);
  //   // TODO recompute nutrition
  // };

  const onCloseMealDetail = () => {
    setPage(PROMPT_PAGE);
  };

  const customerFoodDataID = customerFoodDataIDOfSpace(
    currentSpaceMembership?.spaceID,
  );

  const createMealFromPrompt = async () => {
    const MAX_WAIT_MS = 180000;
    const POLL_INTERVAL_MS = 2000;
    const recipeJobID = await beginLogMealAIOperation(
      prompt,
      customerFoodDataID,
    );
    console.log({ recipeJobID });
    let msWaitedSoFar = 0;
    while (msWaitedSoFar < MAX_WAIT_MS) {
      // eslint-disable-next-line no-await-in-loop
      await awaitableTimeout(POLL_INTERVAL_MS);
      msWaitedSoFar += POLL_INTERVAL_MS;
      // eslint-disable-next-line no-await-in-loop
      const recipeJobResult = await checkLogMealAIOperation(recipeJobID);
      if (recipeJobResult?.jobStatus === JobStatus.FAILED) {
        console.error('Recipe AI job failed');
        // TODO
        return null;
      }
      if (recipeJobResult?.jobStatus === JobStatus.COMPLETED) {
        const newMeal = newMealFromImportedRecipe(recipeJobResult.recipe);
        // Structured ingredient returned by AI generator is not linked to a unique ingredient
        // newMeal.ingredients = newMeal.ingredients.map((ingredient) => ({
        //   ...ingredient,
        //   structuredIngredient: null,
        // }));
        // setMeal(newMeal);
        // TODO
        return newMeal;
      }
    }
    return null;
  };

  const onClickLogPrompt = () => {
    setIsLoading(true);

    tracker(userProfile, [
      {
        name: 'Food item logged using AI',
        args: {
          prompt,
        },
      },
    ]);

    createMealFromPrompt()
      .then((newMeal) => {
        if (newMeal) {
          setMeal(newMeal);
          setPage(MEAL_DETAIL_PAGE);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const onClickLogMealDetail = useCallback(
    async (newMeal, derivedNutrition) => {
      const { data: insertedMeal } = await createMeal(
        newMealWithDefaultFields(
          {
            ...newMeal,
            derivedNutrition,
          },
          plannerWeek.id,
          currentSpaceMembership.id,
        ),
      );

      const updatedPlannerWeek = plannerWeekWithMealAdded(
        plannerWeek,
        insertedMeal,
        dayIndex,
      );

      await updateDbPlannerWeek(updatedPlannerWeek);

      const today = calendarDayFromJSDate(new Date());
      addUserMyDayActionRecord(
        mealCompletedActionRecord(
          currentSpaceMembership?.id,
          currentProgrammeEnrollment?.id,
          insertedMeal.id,
          plannerWeek.id,
          today,
        ),
      );
      const counters = ['numMealsAdded'];
      counters.push('numMealsAddedManually');
      tracker(
        userProfile,
        [
          {
            name: 'Add new meal',
            args: {
              logFoodItem: true,
              section: 'planner',
            },
          },
        ],
        counters,
      );
      onDismiss();
    },
    [
      addUserMyDayActionRecord,
      createMeal,
      currentProgrammeEnrollment?.id,
      currentSpaceMembership.id,
      dayIndex,
      onDismiss,
      plannerWeek,
      tracker,
      updateDbPlannerWeek,
      userProfile,
    ],
  );

  const onChooseRecentlyLoggedDistilledMeal = useCallback(
    async (recentlyLoggedDistilledMeal) => {
      const { data: recentlyLoggedMeal } = await lazyGetMeal(
        recentlyLoggedDistilledMeal.id,
      );
      const clonedMeal = cloneObject(recentlyLoggedMeal);
      return onClickLogMealDetail(clonedMeal, clonedMeal.derivedNutrition);
    },
    [lazyGetMeal, onClickLogMealDetail],
  );

  if (page === PROMPT_PAGE) {
    return (
      <LogFoodItemPromptPage
        prompt={prompt}
        setPrompt={setPrompt}
        isLoading={isLoading}
        recentlyLoggedDistilledMeals={recentlyLoggedDistilledMeals}
        onChooseRecentlyLoggedDistilledMeal={
          onChooseRecentlyLoggedDistilledMeal
        }
        onClickLog={onClickLogPrompt}
        onDismiss={onDismiss}
      />
    );
  }

  if (page === MEAL_DETAIL_PAGE) {
    return (
      <LogFoodItemMealDetailPage
        meal={meal}
        onClickLog={onClickLogMealDetail}
        onCloseMealDetail={onCloseMealDetail}
      />
    );
  }

  return null;
};

LogFoodItemContainer.propTypes = {
  plannerViewWeekStartDate: PropTypes.string.isRequired,
  dayIndex: PropTypes.number.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

export default LogFoodItemContainer;
